/**
 * Sentence Listening Data
 * Sentences for listening practice, organized by difficulty and category
 */

export interface ListeningSentence {
  id: string;
  sentence: string;
  translation: string;
  phonetic?: string;
  category: string;
  difficulty: 'easy' | 'medium' | 'hard';
  grade: '3' | '4' | '5' | '6' | '7' | '8' | '9';
  level: 'elementary' | 'middle';
  keywords: string[]; // Keywords for fill-in-the-blank exercises
  blanks?: number[]; // Positions of words to blank out (index of words)
}

export const listeningSentences: ListeningSentence[] = [
  // ==================== Elementary - Grade 3 (Easy) ====================
  {
    id: 'elem_g3_001',
    sentence: 'This is a book.',
    translation: '这是一本书。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['book'],
    blanks: [3] // "book" is the 4th word (index 3)
  },
  {
    id: 'elem_g3_002',
    sentence: 'I like apples.',
    translation: '我喜欢苹果。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['apples'],
    blanks: [2]
  },
  {
    id: 'elem_g3_003',
    sentence: 'She is my mother.',
    translation: '她是我的妈妈。',
    category: 'family',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['mother'],
    blanks: [3]
  },
  {
    id: 'elem_g3_004',
    sentence: 'The cat is white.',
    translation: '这只猫是白色的。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['white'],
    blanks: [3]
  },
  {
    id: 'elem_g3_005',
    sentence: 'I have a pen.',
    translation: '我有一支钢笔。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['pen'],
    blanks: [3]
  },
  {
    id: 'elem_g3_006',
    sentence: 'Good morning!',
    translation: '早上好！',
    category: 'greetings',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['morning'],
    blanks: [1]
  },
  {
    id: 'elem_g3_007',
    sentence: 'It is sunny today.',
    translation: '今天是晴天。',
    category: 'weather',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['sunny'],
    blanks: [2]
  },
  {
    id: 'elem_g3_008',
    sentence: 'I am a student.',
    translation: '我是一名学生。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['student'],
    blanks: [3]
  },
  {
    id: 'elem_g3_009',
    sentence: 'This is my friend.',
    translation: '这是我的朋友。',
    category: 'social',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['friend'],
    blanks: [3]
  },
  {
    id: 'elem_g3_010',
    sentence: 'We play games.',
    translation: '我们玩游戏。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['games'],
    blanks: [2]
  },

  // ==================== Elementary - Grade 4 (Easy-Medium) ====================
  {
    id: 'elem_g4_001',
    sentence: 'Can I help you?',
    translation: '我能帮你吗？',
    category: 'social',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['help'],
    blanks: [2]
  },
  {
    id: 'elem_g4_002',
    sentence: 'Where is the library?',
    translation: '图书馆在哪里？',
    category: 'places',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['library'],
    blanks: [3]
  },
  {
    id: 'elem_g4_003',
    sentence: 'I get up at seven.',
    translation: '我七点起床。',
    category: 'daily',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['seven'],
    blanks: [4]
  },
  {
    id: 'elem_g4_004',
    sentence: 'She likes reading books.',
    translation: '她喜欢读书。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['reading'],
    blanks: [2]
  },
  {
    id: 'elem_g4_005',
    sentence: 'We have English class.',
    translation: '我们有英语课。',
    category: 'school',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['English'],
    blanks: [2]
  },
  {
    id: 'elem_g4_006',
    sentence: 'The bird can fly.',
    translation: '鸟会飞。',
    category: 'animals',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['fly'],
    blanks: [3]
  },
  {
    id: 'elem_g4_007',
    sentence: 'I do my homework.',
    translation: '我做我的作业。',
    category: 'school',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['homework'],
    blanks: [3]
  },
  {
    id: 'elem_g4_008',
    sentence: 'What time is it?',
    translation: '现在几点了？',
    category: 'time',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['time'],
    blanks: [1]
  },
  {
    id: 'elem_g4_009',
    sentence: 'I want some water.',
    translation: '我想要一些水。',
    category: 'food',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['water'],
    blanks: [3]
  },
  {
    id: 'elem_g4_010',
    sentence: 'Let us go to school.',
    translation: '让我们去学校吧。',
    category: 'school',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['school'],
    blanks: [4]
  },

  // ==================== Elementary - Grade 5 (Medium) ====================
  {
    id: 'elem_g5_001',
    sentence: 'I usually go to school by bus.',
    translation: '我通常坐公交车去学校。',
    category: 'transport',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['bus'],
    blanks: [6]
  },
  {
    id: 'elem_g5_002',
    sentence: 'My favorite subject is math.',
    translation: '我最喜欢的科目是数学。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['math'],
    blanks: [4]
  },
  {
    id: 'elem_g5_003',
    sentence: 'We should protect the environment.',
    translation: '我们应该保护环境。',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['environment'],
    blanks: [4]
  },
  {
    id: 'elem_g5_004',
    sentence: 'I enjoy playing basketball.',
    translation: '我喜欢打篮球。',
    category: 'sports',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['basketball'],
    blanks: [3]
  },
  {
    id: 'elem_g5_005',
    sentence: 'The weather is getting warmer.',
    translation: '天气变暖了。',
    category: 'weather',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['warmer'],
    blanks: [4]
  },
  {
    id: 'elem_g5_006',
    sentence: 'Could you help me please?',
    translation: '你能帮我一下吗？',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['help'],
    blanks: [2]
  },
  {
    id: 'elem_g5_007',
    sentence: 'I brush my teeth every day.',
    translation: '我每天都刷牙。',
    category: 'daily',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['teeth'],
    blanks: [3]
  },
  {
    id: 'elem_g5_008',
    sentence: 'What do you do on weekends?',
    translation: '你周末做什么？',
    category: 'daily',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['weekends'],
    blanks: [5]
  },
  {
    id: 'elem_g5_009',
    sentence: 'There are many books in the library.',
    translation: '图书馆里有很多书。',
    category: 'places',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['books'],
    blanks: [3]
  },
  {
    id: 'elem_g5_010',
    sentence: 'I am interested in science.',
    translation: '我对科学感兴趣。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['science'],
    blanks: [4]
  },

  // ==================== Elementary - Grade 6 (Medium-Hard) ====================
  {
    id: 'elem_g6_001',
    sentence: 'I have been learning English for three years.',
    translation: '我学英语已经三年了。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['English', 'years'],
    blanks: [3, 7]
  },
  {
    id: 'elem_g6_002',
    sentence: 'If it rains tomorrow, we will stay at home.',
    translation: '如果明天下雨，我们就待在家里。',
    category: 'weather',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['rains', 'home'],
    blanks: [2, 8]
  },
  {
    id: 'elem_g6_003',
    sentence: 'My dream is to become a teacher.',
    translation: '我的梦想是成为一名老师。',
    category: 'future',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['teacher'],
    blanks: [6]
  },
  {
    id: 'elem_g6_004',
    sentence: 'We need to work together as a team.',
    translation: '我们需要作为一个团队合作。',
    category: 'social',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['team'],
    blanks: [7]
  },
  {
    id: 'elem_g6_005',
    sentence: 'The internet helps us learn more knowledge.',
    translation: '互联网帮助我们学习更多知识。',
    category: 'technology',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['internet', 'knowledge'],
    blanks: [1, 7]
  },
  {
    id: 'elem_g6_006',
    sentence: 'Reading books can improve our vocabulary.',
    translation: '读书可以增加我们的词汇量。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['vocabulary'],
    blanks: [5]
  },
  {
    id: 'elem_g6_007',
    sentence: 'I prefer tea to coffee.',
    translation: '比起咖啡，我更喜欢茶。',
    category: 'food',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['tea', 'coffee'],
    blanks: [2, 4]
  },
  {
    id: 'elem_g6_008',
    sentence: 'She is good at playing the piano.',
    translation: '她擅长弹钢琴。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['piano'],
    blanks: [6]
  },
  {
    id: 'elem_g6_009',
    sentence: 'We should be kind to animals.',
    translation: '我们应该善待动物。',
    category: 'social',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['animals'],
    blanks: [5]
  },
  {
    id: 'elem_g6_010',
    sentence: 'Exercise is good for our health.',
    translation: '运动对我们的健康有益。',
    category: 'health',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['health'],
    blanks: [5]
  },

  // ==================== Middle School - Grade 7 (Medium-Hard) ====================
  {
    id: 'middle_g7_001',
    sentence: 'I wonder if you could tell me the way.',
    translation: '我想知道你能否告诉我路怎么走。',
    category: 'social',
    difficulty: 'hard',
    grade: '7',
    level: 'middle',
    keywords: ['wonder', 'way'],
    blanks: [1, 8]
  },
  {
    id: 'middle_g7_002',
    sentence: 'The book which I bought yesterday is interesting.',
    translation: '我昨天买的那本书很有趣。',
    category: 'school',
    difficulty: 'hard',
    grade: '7',
    level: 'middle',
    keywords: ['interesting'],
    blanks: [8]
  },
  {
    id: 'middle_g7_003',
    sentence: 'I have never been to Beijing before.',
    translation: '我以前从未去过北京。',
    category: 'travel',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['Beijing'],
    blanks: [5]
  },
  {
    id: 'middle_g7_004',
    sentence: 'It took me two hours to finish the homework.',
    translation: '我花了两个小时完成作业。',
    category: 'school',
    difficulty: 'hard',
    grade: '7',
    level: 'middle',
    keywords: ['hours', 'homework'],
    blanks: [3, 8]
  },
  {
    id: 'middle_g7_005',
    sentence: 'I suggest that we should go by train.',
    translation: '我建议我们应该坐火车去。',
    category: 'travel',
    difficulty: 'hard',
    grade: '7',
    level: 'middle',
    keywords: ['suggest', 'train'],
    blanks: [1, 7]
  },
  {
    id: 'middle_g7_006',
    sentence: 'Protecting the environment is everyone\'s responsibility.',
    translation: '保护环境是每个人的责任。',
    category: 'social',
    difficulty: 'hard',
    grade: '7',
    level: 'middle',
    keywords: ['environment', 'responsibility'],
    blanks: [2, 6]
  },
  {
    id: 'middle_g7_007',
    sentence: 'I used to play football when I was young.',
    translation: '我年轻时经常踢足球。',
    category: 'sports',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['football'],
    blanks: [3]
  },
  {
    id: 'middle_g7_008',
    sentence: 'Neither Tom nor I like swimming.',
    translation: '汤姆和我都不喜欢游泳。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '7',
    level: 'middle',
    keywords: ['swimming'],
    blanks: [6]
  },
  {
    id: 'middle_g7_009',
    sentence: 'I believe that practice makes perfect.',
    translation: '我相信熟能生巧。',
    category: 'wisdom',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['practice', 'perfect'],
    blanks: [3, 5]
  },
  {
    id: 'middle_g7_010',
    sentence: 'The more you read, the more you learn.',
    translation: '你读得越多，学得越多。',
    category: 'wisdom',
    difficulty: 'hard',
    grade: '7',
    level: 'middle',
    keywords: ['read', 'learn'],
    blanks: [3, 7]
  },

  // ==================== Middle School - Grade 8 (Hard) ====================
  {
    id: 'middle_g8_001',
    sentence: 'I regretted not studying harder last term.',
    translation: '我后悔上学期没有更努力学习。',
    category: 'school',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['regretted', 'studying'],
    blanks: [1, 3]
  },
  {
    id: 'middle_g8_002',
    sentence: 'Not only is she smart, but she is also kind.',
    translation: '她不仅聪明，而且善良。',
    category: 'social',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['smart', 'kind'],
    blanks: [4, 9]
  },
  {
    id: 'middle_g8_003',
    sentence: 'It is said that he has won the competition.',
    translation: '据说他赢得了比赛。',
    category: 'achievements',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['competition'],
    blanks: [7]
  },
  {
    id: 'middle_g8_004',
    sentence: 'What matters most is how we treat others.',
    translation: '最重要的是我们如何对待他人。',
    category: 'wisdom',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['matters', 'treat'],
    blanks: [1, 6]
  },
  {
    id: 'middle_g8_005',
    sentence: 'Unless we act now, it will be too late.',
    translation: '除非我们现在行动，否则就太晚了。',
    category: 'wisdom',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['unless', 'late'],
    blanks: [0, 8]
  },
  {
    id: 'middle_g8_006',
    sentence: 'The problem is too difficult for me to solve.',
    translation: '这个问题对我来说太难解决了。',
    category: 'school',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['difficult', 'solve'],
    blanks: [4, 9]
  },
  {
    id: 'middle_g8_007',
    sentence: 'I am looking forward to seeing you again.',
    translation: '我期待再次见到你。',
    category: 'social',
    difficulty: 'medium',
    grade: '8',
    level: 'middle',
    keywords: ['forward'],
    blanks: [4]
  },
  {
    id: 'middle_g8_008',
    sentence: 'She asked me where I had been.',
    translation: '她问我去了哪里。',
    category: 'social',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['where'],
    blanks: [4]
  },
  {
    id: 'middle_g8_009',
    sentence: 'It was such a beautiful day that we went hiking.',
    translation: '天气太好了，我们去徒步旅行了。',
    category: 'activities',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['beautiful', 'hiking'],
    blanks: [3, 9]
  },
  {
    id: 'middle_g8_010',
    sentence: 'Hardly had I arrived when it started to rain.',
    translation: '我刚到，天就开始下雨了。',
    category: 'weather',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['hardly', 'rain'],
    blanks: [0, 9]
  },

  // ==================== Middle School - Grade 9 (Hard) ====================
  {
    id: 'middle_g9_001',
    sentence: 'Had I known earlier, I would have told you.',
    translation: '如果我早点知道，我就告诉你了。',
    category: 'social',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['known', 'told'],
    blanks: [1, 6]
  },
  {
    id: 'middle_g9_002',
    sentence: 'So fast did he run that no one could catch him.',
    translation: '他跑得太快了，没人能追上他。',
    category: 'sports',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['fast', 'catch'],
    blanks: [1, 9]
  },
  {
    id: 'middle_g9_003',
    sentence: 'Whatever you do, do it with all your heart.',
    translation: '无论你做什么，都要全心全意。',
    category: 'wisdom',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['whatever', 'heart'],
    blanks: [0, 8]
  },
  {
    id: 'middle_g9_004',
    sentence: 'The reason why he failed is that he was lazy.',
    translation: '他失败的原因是他很懒。',
    category: 'wisdom',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['failed', 'lazy'],
    blanks: [3, 9]
  },
  {
    id: 'middle_g9_005',
    sentence: 'It is high time that we took action.',
    translation: '是我们采取行动的时候了。',
    category: 'wisdom',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['action'],
    blanks: [7]
  },
  {
    id: 'middle_g9_006',
    sentence: 'Little did I know what would happen next.',
    translation: '我完全不知道接下来会发生什么。',
    category: 'social',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['little', 'happen'],
    blanks: [0, 6]
  },
  {
    id: 'middle_g9_007',
    sentence: 'Only by working hard can we achieve our goals.',
    translation: '只有通过努力工作，我们才能实现目标。',
    category: 'wisdom',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['working', 'achieve'],
    blanks: [2, 7]
  },
  {
    id: 'middle_g9_008',
    sentence: 'It was in the library that I met my best friend.',
    translation: '正是在图书馆我遇到了我最好的朋友。',
    category: 'social',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['library', 'friend'],
    blanks: [3, 10]
  },
  {
    id: 'middle_g9_009',
    sentence: 'Never before have I seen such a beautiful sunset.',
    translation: '我从未见过如此美丽的日落。',
    category: 'nature',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['beautiful', 'sunset'],
    blanks: [5, 7]
  },
  {
    id: 'middle_g9_010',
    sentence: 'Success comes to those who work hard and never give up.',
    translation: '成功属于那些努力工作且永不放弃的人。',
    category: 'wisdom',
    difficulty: 'hard',
    grade: '9',
    level: 'middle',
    keywords: ['success', 'never'],
    blanks: [0, 9]
  },

  // ==================== Elementary - More Daily Life ====================
  {
    id: 'elem_daily_001',
    sentence: 'I wake up early in the morning.',
    translation: '我早上醒得很早。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['early'],
    blanks: [3]
  },
  {
    id: 'elem_daily_002',
    sentence: 'My father goes to work by car.',
    translation: '我爸爸开车去上班。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['car'],
    blanks: [6]
  },
  {
    id: 'elem_daily_003',
    sentence: 'We eat dinner together.',
    translation: '我们一起吃晚饭。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['dinner'],
    blanks: [2]
  },
  {
    id: 'elem_daily_004',
    sentence: 'I help my mom cook.',
    translation: '我帮妈妈做饭。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['cook'],
    blanks: [4]
  },
  {
    id: 'elem_daily_005',
    sentence: 'The dog runs in the garden.',
    translation: '狗在花园里跑。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['garden'],
    blanks: [5]
  },
  {
    id: 'elem_daily_006',
    sentence: 'I drink milk every day.',
    translation: '我每天都喝牛奶。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['milk'],
    blanks: [2]
  },
  {
    id: 'elem_daily_007',
    sentence: 'She sings beautifully.',
    translation: '她唱得很美。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['sings'],
    blanks: [1]
  },
  {
    id: 'elem_daily_008',
    sentence: 'My sister is drawing a picture.',
    translation: '我妹妹在画画。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['drawing'],
    blanks: [3]
  },
  {
    id: 'elem_daily_009',
    sentence: 'We visit grandparents on Sunday.',
    translation: '我们周日看望祖父母。',
    category: 'family',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['grandparents'],
    blanks: [2]
  },
  {
    id: 'elem_daily_010',
    sentence: 'I write in my notebook.',
    translation: '我在笔记本上写字。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['notebook'],
    blanks: [4]
  },

  // ==================== Elementary - More School ====================
  {
    id: 'elem_school_001',
    sentence: 'The teacher asks a question.',
    translation: '老师问了一个问题。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['question'],
    blanks: [4]
  },
  {
    id: 'elem_school_002',
    sentence: 'We learn English songs.',
    translation: '我们学习英语歌曲。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['songs'],
    blanks: [3]
  },
  {
    id: 'elem_school_003',
    sentence: 'I raise my hand.',
    translation: '我举手。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['hand'],
    blanks: [3]
  },
  {
    id: 'elem_school_004',
    sentence: 'Please close the door.',
    translation: '请关门。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['door'],
    blanks: [3]
  },
  {
    id: 'elem_school_005',
    sentence: 'I borrow books from the library.',
    translation: '我从图书馆借书。',
    category: 'school',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['library'],
    blanks: [6]
  },
  {
    id: 'elem_school_006',
    sentence: 'We have a music class today.',
    translation: '我们今天有音乐课。',
    category: 'school',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['music'],
    blanks: [3]
  },
  {
    id: 'elem_school_007',
    sentence: 'My pencil is broken.',
    translation: '我的铅笔断了。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['pencil'],
    blanks: [1]
  },
  {
    id: 'elem_school_008',
    sentence: 'I sit next to my friend.',
    translation: '我坐在朋友旁边。',
    category: 'school',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['next'],
    blanks: [2]
  },
  {
    id: 'elem_school_009',
    sentence: 'We play during the break.',
    translation: '我们在课间休息时玩耍。',
    category: 'school',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['break'],
    blanks: [5]
  },
  {
    id: 'elem_school_010',
    sentence: 'The exam is next week.',
    translation: '考试在下周。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['exam'],
    blanks: [1]
  },

  // ==================== Elementary - More Food ====================
  {
    id: 'elem_food_001',
    sentence: 'I eat eggs for breakfast.',
    translation: '我早餐吃鸡蛋。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['eggs'],
    blanks: [2]
  },
  {
    id: 'elem_food_002',
    sentence: 'Rice is my favorite food.',
    translation: '米饭是我最喜欢的食物。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['rice'],
    blanks: [0]
  },
  {
    id: 'elem_food_003',
    sentence: 'My mom cooks delicious soup.',
    translation: '我妈妈煮的汤很好喝。',
    category: 'food',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['soup'],
    blanks: [4]
  },
  {
    id: 'elem_food_004',
    sentence: 'Vegetables are healthy.',
    translation: '蔬菜很健康。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['vegetables'],
    blanks: [0]
  },
  {
    id: 'elem_food_005',
    sentence: 'I drink orange juice.',
    translation: '我喝橙汁。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['juice'],
    blanks: [2]
  },
  {
    id: 'elem_food_006',
    sentence: 'The cake tastes sweet.',
    translation: '蛋糕尝起来很甜。',
    category: 'food',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['sweet'],
    blanks: [3]
  },
  {
    id: 'elem_food_007',
    sentence: 'We buy bread at the bakery.',
    translation: '我们在面包店买面包。',
    category: 'food',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['bakery'],
    blanks: [5]
  },
  {
    id: 'elem_food_008',
    sentence: 'I want some noodles.',
    translation: '我想要一些面条。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['noodles'],
    blanks: [3]
  },
  {
    id: 'elem_food_009',
    sentence: 'Fish is good for you.',
    translation: '鱼对你有好处。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['fish'],
    blanks: [0]
  },
  {
    id: 'elem_food_010',
    sentence: 'I do not like spicy food.',
    translation: '我不喜欢辣的食物。',
    category: 'food',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['spicy'],
    blanks: [4]
  },

  // ==================== Elementary - Weather & Seasons ====================
  {
    id: 'elem_weather_001',
    sentence: 'It is raining outside.',
    translation: '外面在下雨。',
    category: 'weather',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['raining'],
    blanks: [2]
  },
  {
    id: 'elem_weather_002',
    sentence: 'The sun is bright today.',
    translation: '今天阳光明媚。',
    category: 'weather',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['sun'],
    blanks: [1]
  },
  {
    id: 'elem_weather_003',
    sentence: 'Winter is very cold.',
    translation: '冬天很冷。',
    category: 'weather',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['winter'],
    blanks: [0]
  },
  {
    id: 'elem_weather_004',
    sentence: 'I like summer vacation.',
    translation: '我喜欢暑假。',
    category: 'weather',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['summer'],
    blanks: [2]
  },
  {
    id: 'elem_weather_005',
    sentence: 'The wind is strong.',
    translation: '风很大。',
    category: 'weather',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['wind'],
    blanks: [1]
  },
  {
    id: 'elem_weather_006',
    sentence: 'There are clouds in the sky.',
    translation: '天上有云。',
    category: 'weather',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['clouds'],
    blanks: [2]
  },
  {
    id: 'elem_weather_007',
    sentence: 'It is hot in July.',
    translation: '七月很热。',
    category: 'weather',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['hot'],
    blanks: [2]
  },
  {
    id: 'elem_weather_008',
    sentence: 'We need an umbrella.',
    translation: '我们需要一把雨伞。',
    category: 'weather',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['umbrella'],
    blanks: [3]
  },
  {
    id: 'elem_weather_009',
    sentence: 'Spring is beautiful.',
    translation: '春天很美。',
    category: 'weather',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['spring'],
    blanks: [0]
  },
  {
    id: 'elem_weather_010',
    sentence: 'Leaves fall in autumn.',
    translation: '秋天树叶落下。',
    category: 'weather',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['autumn'],
    blanks: [4]
  },

  // ==================== Elementary - Places & Directions ====================
  {
    id: 'elem_places_001',
    sentence: 'The park is near my home.',
    translation: '公园在我家附近。',
    category: 'places',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['park'],
    blanks: [1]
  },
  {
    id: 'elem_places_002',
    sentence: 'I go to the hospital.',
    translation: '我去医院。',
    category: 'places',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['hospital'],
    blanks: [4]
  },
  {
    id: 'elem_places_003',
    sentence: 'The supermarket is big.',
    translation: '超市很大。',
    category: 'places',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['supermarket'],
    blanks: [1]
  },
  {
    id: 'elem_places_004',
    sentence: 'Turn left at the corner.',
    translation: '在拐角处左转。',
    category: 'places',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['left'],
    blanks: [1]
  },
  {
    id: 'elem_places_005',
    sentence: 'Walk straight ahead.',
    translation: '直走。',
    category: 'places',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['straight'],
    blanks: [1]
  },
  {
    id: 'elem_places_006',
    sentence: 'The museum is on the right.',
    translation: '博物馆在右边。',
    category: 'places',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['museum', 'right'],
    blanks: [1, 5]
  },
  {
    id: 'elem_places_007',
    sentence: 'I live on Green Street.',
    translation: '我住在绿街。',
    category: 'places',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['street'],
    blanks: [4]
  },
  {
    id: 'elem_places_008',
    sentence: 'The cinema is next to the bank.',
    translation: '电影院在银行旁边。',
    category: 'places',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['cinema', 'bank'],
    blanks: [1, 6]
  },
  {
    id: 'elem_places_009',
    sentence: 'Excuse me, where is the station?',
    translation: '打扰一下，车站在哪里？',
    category: 'places',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['station'],
    blanks: [4]
  },
  {
    id: 'elem_places_010',
    sentence: 'Go across the bridge.',
    translation: '穿过桥。',
    category: 'places',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['bridge'],
    blanks: [3]
  },

  // ==================== Elementary - More Animals ====================
  {
    id: 'elem_animals_001',
    sentence: 'The bird is singing.',
    translation: '鸟在唱歌。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['bird'],
    blanks: [1]
  },
  {
    id: 'elem_animals_002',
    sentence: 'I have a pet dog.',
    translation: '我有一只宠物狗。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['dog'],
    blanks: [4]
  },
  {
    id: 'elem_animals_003',
    sentence: 'Cats like to sleep.',
    translation: '猫喜欢睡觉。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['cats'],
    blanks: [0]
  },
  {
    id: 'elem_animals_004',
    sentence: 'The rabbit has long ears.',
    translation: '兔子有长耳朵。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['rabbit'],
    blanks: [1]
  },
  {
    id: 'elem_animals_005',
    sentence: 'Fish swim in the water.',
    translation: '鱼在水里游。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['fish'],
    blanks: [0]
  },
  {
    id: 'elem_animals_006',
    sentence: 'The elephant is huge.',
    translation: '大象很大。',
    category: 'animals',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['elephant'],
    blanks: [1]
  },
  {
    id: 'elem_animals_007',
    sentence: 'A tiger is a wild animal.',
    translation: '老虎是野生动物。',
    category: 'animals',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['tiger'],
    blanks: [1]
  },
  {
    id: 'elem_animals_008',
    sentence: 'The panda eats bamboo.',
    translation: '熊猫吃竹子。',
    category: 'animals',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['panda', 'bamboo'],
    blanks: [1, 3]
  },
  {
    id: 'elem_animals_009',
    sentence: 'Horses can run fast.',
    translation: '马跑得很快。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['horses'],
    blanks: [0]
  },
  {
    id: 'elem_animals_010',
    sentence: 'The monkey climbs trees.',
    translation: '猴子爬树。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['monkey'],
    blanks: [1]
  },

  // ==================== Middle School - More Complex Sentences ====================
  {
    id: 'middle_complex_001',
    sentence: 'I have been waiting for two hours.',
    translation: '我已经等了两个小时。',
    category: 'daily',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['waiting'],
    blanks: [2]
  },
  {
    id: 'middle_complex_002',
    sentence: 'She asked me if I had finished my homework.',
    translation: '她问我是否完成了作业。',
    category: 'school',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['finished'],
    blanks: [6]
  },
  {
    id: 'middle_complex_003',
    sentence: 'The book which you gave me is very interesting.',
    translation: '你给我的那本书很有趣。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['interesting'],
    blanks: [9]
  },
  {
    id: 'middle_complex_004',
    sentence: 'I wonder where he has gone.',
    translation: '我想知道他去哪了。',
    category: 'social',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['wonder'],
    blanks: [1]
  },
  {
    id: 'middle_complex_005',
    sentence: 'It is important to learn a second language.',
    translation: '学习第二语言很重要。',
    category: 'school',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['language'],
    blanks: [7]
  },
  {
    id: 'middle_complex_006',
    sentence: 'The more you practice, the better you become.',
    translation: '你练习得越多，变得越好。',
    category: 'wisdom',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['practice', 'better'],
    blanks: [3, 6]
  },
  {
    id: 'middle_complex_007',
    sentence: 'I prefer reading to watching TV.',
    translation: '比起看电视，我更喜欢读书。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['reading'],
    blanks: [2]
  },
  {
    id: 'middle_complex_008',
    sentence: 'She speaks English as well as Chinese.',
    translation: '她说英语和中文一样好。',
    category: 'school',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['English', 'Chinese'],
    blanks: [2, 6]
  },
  {
    id: 'middle_complex_009',
    sentence: 'I did not know what to do.',
    translation: '我不知道该做什么。',
    category: 'social',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['know'],
    blanks: [3]
  },
  {
    id: 'middle_complex_010',
    sentence: 'He asked me not to be late.',
    translation: '他叫我不要迟到。',
    category: 'social',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['late'],
    blanks: [6]
  },

  // ==================== Middle School - Technology & Science ====================
  {
    id: 'middle_tech_001',
    sentence: 'Computers help us learn faster.',
    translation: '计算机帮助我们学得更快。',
    category: 'technology',
    difficulty: 'medium',
    grade: '6',
    level: 'middle',
    keywords: ['computers'],
    blanks: [0]
  },
  {
    id: 'middle_tech_002',
    sentence: 'The internet connects people around the world.',
    translation: '互联网连接世界各地的人。',
    category: 'technology',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['internet', 'world'],
    blanks: [1, 7]
  },
  {
    id: 'middle_tech_003',
    sentence: 'Scientists study the universe.',
    translation: '科学家研究宇宙。',
    category: 'technology',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['scientists', 'universe'],
    blanks: [0, 4]
  },
  {
    id: 'middle_tech_004',
    sentence: 'Smartphones are very useful.',
    translation: '智能手机非常有用。',
    category: 'technology',
    difficulty: 'medium',
    grade: '6',
    level: 'middle',
    keywords: ['smartphones'],
    blanks: [0]
  },
  {
    id: 'middle_tech_005',
    sentence: 'Robots can do many jobs.',
    translation: '机器人可以做很多工作。',
    category: 'technology',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['robots'],
    blanks: [0]
  },
  {
    id: 'middle_tech_006',
    sentence: 'We should use technology wisely.',
    translation: '我们应该明智地使用技术。',
    category: 'technology',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['technology'],
    blanks: [3]
  },
  {
    id: 'middle_tech_007',
    sentence: 'The earth goes around the sun.',
    translation: '地球绕着太阳转。',
    category: 'nature',
    difficulty: 'medium',
    grade: '6',
    level: 'middle',
    keywords: ['earth', 'sun'],
    blanks: [1, 5]
  },
  {
    id: 'middle_tech_008',
    sentence: 'Plants need sunlight to grow.',
    translation: '植物需要阳光才能生长。',
    category: 'nature',
    difficulty: 'easy',
    grade: '5',
    level: 'elementary',
    keywords: ['sunlight'],
    blanks: [2]
  },
  {
    id: 'middle_tech_009',
    sentence: 'Water is essential for life.',
    translation: '水对生命至关重要。',
    category: 'nature',
    difficulty: 'medium',
    grade: '6',
    level: 'middle',
    keywords: ['water'],
    blanks: [0]
  },
  {
    id: 'middle_tech_010',
    sentence: 'We must protect our environment.',
    translation: '我们必须保护我们的环境。',
    category: 'nature',
    difficulty: 'medium',
    grade: '6',
    level: 'middle',
    keywords: ['protect', 'environment'],
    blanks: [2, 4]
  },

  // ==================== Middle School - Emotions & Feelings ====================
  {
    id: 'middle_emotion_001',
    sentence: 'I feel happy when I help others.',
    translation: '帮助别人让我感到快乐。',
    category: 'social',
    difficulty: 'medium',
    grade: '6',
    level: 'middle',
    keywords: ['happy'],
    blanks: [2]
  },
  {
    id: 'middle_emotion_002',
    sentence: 'She was worried about the exam.',
    translation: '她担心考试。',
    category: 'school',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['worried'],
    blanks: [2]
  },
  {
    id: 'middle_emotion_003',
    sentence: 'I am proud of my progress.',
    translation: '我为我的进步感到自豪。',
    category: 'wisdom',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['proud', 'progress'],
    blanks: [2, 5]
  },
  {
    id: 'middle_emotion_004',
    sentence: 'He felt nervous before the speech.',
    translation: '演讲前他感到紧张。',
    category: 'social',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['nervous'],
    blanks: [2]
  },
  {
    id: 'middle_emotion_005',
    sentence: 'I was surprised to see him.',
    translation: '见到他很惊讶。',
    category: 'social',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['surprised'],
    blanks: [2]
  },
  {
    id: 'middle_emotion_006',
    sentence: 'She felt disappointed with the result.',
    translation: '她对结果感到失望。',
    category: 'achievements',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['disappointed'],
    blanks: [2]
  },
  {
    id: 'middle_emotion_007',
    sentence: 'I am grateful for your help.',
    translation: '我很感激你的帮助。',
    category: 'social',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['grateful'],
    blanks: [2]
  },
  {
    id: 'middle_emotion_008',
    sentence: 'He felt embarrassed to speak.',
    translation: '他不好意思说话。',
    category: 'social',
    difficulty: 'hard',
    grade: '8',
    level: 'middle',
    keywords: ['embarrassed'],
    blanks: [2]
  },
  {
    id: 'middle_emotion_009',
    sentence: 'I am satisfied with my work.',
    translation: '我对我的工作感到满意。',
    category: 'achievements',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['satisfied'],
    blanks: [2]
  },
  {
    id: 'middle_emotion_010',
    sentence: 'She was excited about the trip.',
    translation: '她对这次旅行感到兴奋。',
    category: 'travel',
    difficulty: 'medium',
    grade: '7',
    level: 'middle',
    keywords: ['excited'],
    blanks: [2]
  },

  // ==================== Elementary Grade 3 - Expanded (Target: 80+ sentences) ====================
  // Numbers and Counting
  {
    id: 'elem_g3_num_001',
    sentence: 'I can count to ten.',
    translation: '我能数到十。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['count'],
    blanks: [2]
  },
  {
    id: 'elem_g3_num_002',
    sentence: 'There are five apples.',
    translation: '有五个苹果。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['five'],
    blanks: [2]
  },
  {
    id: 'elem_g3_num_003',
    sentence: 'I have two hands.',
    translation: '我有两只手。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['two'],
    blanks: [2]
  },
  {
    id: 'elem_g3_num_004',
    sentence: 'Seven plus three is ten.',
    translation: '七加三等于十。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['seven', 'ten'],
    blanks: [0, 4]
  },
  {
    id: 'elem_g3_num_005',
    sentence: 'I see three birds.',
    translation: '我看到三只鸟。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['three'],
    blanks: [2]
  },

  // Colors
  {
    id: 'elem_g3_color_001',
    sentence: 'The sky is blue.',
    translation: '天空是蓝色的。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['blue'],
    blanks: [3]
  },
  {
    id: 'elem_g3_color_002',
    sentence: 'I like red apples.',
    translation: '我喜欢红苹果。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['red'],
    blanks: [2]
  },
  {
    id: 'elem_g3_color_003',
    sentence: 'The grass is green.',
    translation: '草是绿色的。',
    category: 'nature',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['green'],
    blanks: [3]
  },
  {
    id: 'elem_g3_color_004',
    sentence: 'My bag is yellow.',
    translation: '我的书包是黄色的。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['yellow'],
    blanks: [3]
  },
  {
    id: 'elem_g3_color_005',
    sentence: 'I want a pink dress.',
    translation: '我想要一件粉色的裙子。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['pink'],
    blanks: [3]
  },

  // Body Parts
  {
    id: 'elem_g3_body_001',
    sentence: 'I have two eyes.',
    translation: '我有两只眼睛。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['eyes'],
    blanks: [3]
  },
  {
    id: 'elem_g3_body_002',
    sentence: 'Touch your nose.',
    translation: '摸摸你的鼻子。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['nose'],
    blanks: [2]
  },
  {
    id: 'elem_g3_body_003',
    sentence: 'My ears are big.',
    translation: '我的耳朵很大。',
    category: 'daily',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['ears'],
    blanks: [1]
  },
  {
    id: 'elem_g3_body_004',
    sentence: 'Clap your hands.',
    translation: '拍拍你的手。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['hands'],
    blanks: [2]
  },
  {
    id: 'elem_g3_body_005',
    sentence: 'Stamp your feet.',
    translation: '跺跺你的脚。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['feet'],
    blanks: [2]
  },

  // Family Members
  {
    id: 'elem_g3_fam_001',
    sentence: 'This is my father.',
    translation: '这是我的爸爸。',
    category: 'family',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['father'],
    blanks: [3]
  },
  {
    id: 'elem_g3_fam_002',
    sentence: 'My mother is kind.',
    translation: '我的妈妈很善良。',
    category: 'family',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['mother'],
    blanks: [1]
  },
  {
    id: 'elem_g3_fam_003',
    sentence: 'I love my sister.',
    translation: '我爱我的妹妹。',
    category: 'family',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['sister'],
    blanks: [3]
  },
  {
    id: 'elem_g3_fam_004',
    sentence: 'My brother is tall.',
    translation: '我的哥哥很高。',
    category: 'family',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['brother'],
    blanks: [1]
  },
  {
    id: 'elem_g3_fam_005',
    sentence: 'Grandma tells stories.',
    translation: '奶奶讲故事。',
    category: 'family',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['grandma'],
    blanks: [0]
  },

  // Actions and Movements
  {
    id: 'elem_g3_act_001',
    sentence: 'I can run fast.',
    translation: '我能跑得很快。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['run'],
    blanks: [2]
  },
  {
    id: 'elem_g3_act_002',
    sentence: 'She likes to jump.',
    translation: '她喜欢跳。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['jump'],
    blanks: [3]
  },
  {
    id: 'elem_g3_act_003',
    sentence: 'We swim in summer.',
    translation: '我们夏天游泳。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['swim'],
    blanks: [1]
  },
  {
    id: 'elem_g3_act_004',
    sentence: 'The boy can dance.',
    translation: '那个男孩会跳舞。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['dance'],
    blanks: [3]
  },
  {
    id: 'elem_g3_act_005',
    sentence: 'I draw a picture.',
    translation: '我画了一幅画。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['draw'],
    blanks: [1]
  },

  // More School Items
  {
    id: 'elem_g3_sch_001',
    sentence: 'This is a ruler.',
    translation: '这是一把尺子。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['ruler'],
    blanks: [3]
  },
  {
    id: 'elem_g3_sch_002',
    sentence: 'I have an eraser.',
    translation: '我有一块橡皮。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['eraser'],
    blanks: [3]
  },
  {
    id: 'elem_g3_sch_003',
    sentence: 'The bag is heavy.',
    translation: '书包很重。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['bag'],
    blanks: [1]
  },
  {
    id: 'elem_g3_sch_004',
    sentence: 'Please open the window.',
    translation: '请打开窗户。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['window'],
    blanks: [3]
  },
  {
    id: 'elem_g3_sch_005',
    sentence: 'Close your book.',
    translation: '合上你的书。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['book'],
    blanks: [2]
  },

  // More Food Items
  {
    id: 'elem_g3_food_001',
    sentence: 'I eat an apple.',
    translation: '我吃一个苹果。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['apple'],
    blanks: [3]
  },
  {
    id: 'elem_g3_food_002',
    sentence: 'Do you like bananas?',
    translation: '你喜欢香蕉吗？',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['bananas'],
    blanks: [3]
  },
  {
    id: 'elem_g3_food_003',
    sentence: 'Water is healthy.',
    translation: '水很健康。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['water'],
    blanks: [0]
  },
  {
    id: 'elem_g3_food_004',
    sentence: 'The orange is sweet.',
    translation: '橙子很甜。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['orange'],
    blanks: [1]
  },
  {
    id: 'elem_g3_food_005',
    sentence: 'I want some cake.',
    translation: '我想要一些蛋糕。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['cake'],
    blanks: [3]
  },

  // More Animals
  {
    id: 'elem_g3_anim_001',
    sentence: 'The pig is pink.',
    translation: '猪是粉色的。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['pig'],
    blanks: [1]
  },
  {
    id: 'elem_g3_anim_002',
    sentence: 'I see a duck.',
    translation: '我看到一只鸭子。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['duck'],
    blanks: [3]
  },
  {
    id: 'elem_g3_anim_003',
    sentence: 'The chicken is small.',
    translation: '鸡很小。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['chicken'],
    blanks: [1]
  },
  {
    id: 'elem_g3_anim_004',
    sentence: 'She has a cat.',
    translation: '她有一只猫。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['cat'],
    blanks: [3]
  },
  {
    id: 'elem_g3_anim_005',
    sentence: 'The dog runs fast.',
    translation: '狗跑得很快。',
    category: 'animals',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['dog'],
    blanks: [1]
  },

  // Time Concepts
  {
    id: 'elem_g3_time_001',
    sentence: 'It is eight oclock.',
    translation: '现在是八点钟。',
    category: 'time',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['eight'],
    blanks: [2]
  },
  {
    id: 'elem_g3_time_002',
    sentence: 'I go to school today.',
    translation: '我今天去上学。',
    category: 'school',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['today'],
    blanks: [5]
  },
  {
    id: 'elem_g3_time_003',
    sentence: 'We play tomorrow.',
    translation: '我们明天玩。',
    category: 'activities',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['tomorrow'],
    blanks: [2]
  },
  {
    id: 'elem_g3_time_004',
    sentence: 'Yesterday was Sunday.',
    translation: '昨天是星期日。',
    category: 'time',
    difficulty: 'medium',
    grade: '3',
    level: 'elementary',
    keywords: ['yesterday'],
    blanks: [0]
  },
  {
    id: 'elem_g3_time_005',
    sentence: 'It is time for lunch.',
    translation: '该吃午饭了。',
    category: 'food',
    difficulty: 'easy',
    grade: '3',
    level: 'elementary',
    keywords: ['lunch'],
    blanks: [4]
  },

  // ==================== Elementary Grade 4 - Expanded (Target: 80+ sentences) ====================
  // More Daily Routines
  {
    id: 'elem_g4_daily_001',
    sentence: 'I wash my face.',
    translation: '我洗脸。',
    category: 'daily',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['wash'],
    blanks: [1]
  },
  {
    id: 'elem_g4_daily_002',
    sentence: 'She brushes her hair.',
    translation: '她梳头。',
    category: 'daily',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['brushes'],
    blanks: [1]
  },
  {
    id: 'elem_g4_daily_003',
    sentence: 'We eat lunch at noon.',
    translation: '我们中午吃午饭。',
    category: 'daily',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['noon'],
    blanks: [5]
  },
  {
    id: 'elem_g4_daily_004',
    sentence: 'I come home at five.',
    translation: '我五点回家。',
    category: 'daily',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['five'],
    blanks: [4]
  },
  {
    id: 'elem_g4_daily_005',
    sentence: 'My dad reads newspapers.',
    translation: '我爸爸看报纸。',
    category: 'daily',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['newspapers'],
    blanks: [3]
  },

  // More School Activities
  {
    id: 'elem_g4_sch_001',
    sentence: 'We study math together.',
    translation: '我们一起学数学。',
    category: 'school',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['math'],
    blanks: [2]
  },
  {
    id: 'elem_g4_sch_002',
    sentence: 'I listen to the teacher.',
    translation: '我听老师讲课。',
    category: 'school',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['listen'],
    blanks: [1]
  },
  {
    id: 'elem_g4_sch_003',
    sentence: 'Please write your name.',
    translation: '请写上你的名字。',
    category: 'school',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['write'],
    blanks: [1]
  },
  {
    id: 'elem_g4_sch_004',
    sentence: 'The test is easy.',
    translation: '考试很简单。',
    category: 'school',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['test'],
    blanks: [1]
  },
  {
    id: 'elem_g4_sch_005',
    sentence: 'I finish my work.',
    translation: '我完成了我的作业。',
    category: 'school',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['finish'],
    blanks: [1]
  },

  // Weather and Seasons
  {
    id: 'elem_g4_wea_001',
    sentence: 'It is cold in winter.',
    translation: '冬天很冷。',
    category: 'weather',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['cold', 'winter'],
    blanks: [2, 4]
  },
  {
    id: 'elem_g4_wea_002',
    sentence: 'Summer is very hot.',
    translation: '夏天很热。',
    category: 'weather',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['summer', 'hot'],
    blanks: [0, 3]
  },
  {
    id: 'elem_g4_wea_003',
    sentence: 'It is raining now.',
    translation: '现在在下雨。',
    category: 'weather',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['raining'],
    blanks: [2]
  },
  {
    id: 'elem_g4_wea_004',
    sentence: 'We need warm clothes.',
    translation: '我们需要暖和的衣服。',
    category: 'weather',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['warm'],
    blanks: [2]
  },
  {
    id: 'elem_g4_wea_005',
    sentence: 'The snow is white.',
    translation: '雪是白色的。',
    category: 'weather',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['snow'],
    blanks: [1]
  },

  // Clothes and Appearance
  {
    id: 'elem_g4_clo_001',
    sentence: 'I wear a shirt.',
    translation: '我穿衬衫。',
    category: 'daily',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['shirt'],
    blanks: [3]
  },
  {
    id: 'elem_g4_clo_002',
    sentence: 'She has long hair.',
    translation: '她有长头发。',
    category: 'daily',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['hair'],
    blanks: [3]
  },
  {
    id: 'elem_g4_clo_003',
    sentence: 'My shoes are new.',
    translation: '我的鞋子是新的。',
    category: 'daily',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['shoes'],
    blanks: [1]
  },
  {
    id: 'elem_g4_clo_004',
    sentence: 'He is wearing a hat.',
    translation: '他戴着帽子。',
    category: 'daily',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['hat'],
    blanks: [4]
  },
  {
    id: 'elem_g4_clo_005',
    sentence: 'I like my new coat.',
    translation: '我喜欢我的新外套。',
    category: 'daily',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['coat'],
    blanks: [4]
  },

  // Places in Community
  {
    id: 'elem_g4_pla_001',
    sentence: 'We go to the hospital.',
    translation: '我们去医院。',
    category: 'places',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['hospital'],
    blanks: [4]
  },
  {
    id: 'elem_g4_pla_002',
    sentence: 'I play in the park.',
    translation: '我在公园里玩。',
    category: 'places',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['park'],
    blanks: [4]
  },
  {
    id: 'elem_g4_pla_003',
    sentence: 'The shop is open.',
    translation: '商店开门了。',
    category: 'places',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['shop'],
    blanks: [1]
  },
  {
    id: 'elem_g4_pla_004',
    sentence: 'We visit the zoo.',
    translation: '我们参观动物园。',
    category: 'places',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['zoo'],
    blanks: [3]
  },
  {
    id: 'elem_g4_pla_005',
    sentence: 'My house is big.',
    translation: '我的房子很大。',
    category: 'places',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['house'],
    blanks: [1]
  },

  // Transportation
  {
    id: 'elem_g4_trans_001',
    sentence: 'I go by bus.',
    translation: '我坐公交车去。',
    category: 'transport',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['bus'],
    blanks: [3]
  },
  {
    id: 'elem_g4_trans_002',
    sentence: 'The train is fast.',
    translation: '火车很快。',
    category: 'transport',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['train'],
    blanks: [1]
  },
  {
    id: 'elem_g4_trans_003',
    sentence: 'My dad drives a car.',
    translation: '我爸爸开车。',
    category: 'transport',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['car'],
    blanks: [4]
  },
  {
    id: 'elem_g4_trans_004',
    sentence: 'We ride bikes.',
    translation: '我们骑自行车。',
    category: 'transport',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['bikes'],
    blanks: [2]
  },
  {
    id: 'elem_g4_trans_005',
    sentence: 'The plane flies high.',
    translation: '飞机飞得很高。',
    category: 'transport',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['plane'],
    blanks: [1]
  },

  // Health and Body
  {
    id: 'elem_g4_health_001',
    sentence: 'I have a cold.',
    translation: '我感冒了。',
    category: 'health',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['cold'],
    blanks: [3]
  },
  {
    id: 'elem_g4_health_002',
    sentence: 'She feels sick.',
    translation: '她生病了。',
    category: 'health',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['sick'],
    blanks: [2]
  },
  {
    id: 'elem_g4_health_003',
    sentence: 'Take some medicine.',
    translation: '吃些药。',
    category: 'health',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['medicine'],
    blanks: [2]
  },
  {
    id: 'elem_g4_health_004',
    sentence: 'I need to rest.',
    translation: '我需要休息。',
    category: 'health',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['rest'],
    blanks: [3]
  },
  {
    id: 'elem_g4_health_005',
    sentence: 'Drink more water.',
    translation: '多喝水。',
    category: 'health',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['water'],
    blanks: [2]
  },

  // Hobbies and Interests
  {
    id: 'elem_g4_hob_001',
    sentence: 'I collect stamps.',
    translation: '我收集邮票。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['stamps'],
    blanks: [2]
  },
  {
    id: 'elem_g4_hob_002',
    sentence: 'She plays the piano.',
    translation: '她弹钢琴。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['piano'],
    blanks: [3]
  },
  {
    id: 'elem_g4_hob_003',
    sentence: 'We fly kites.',
    translation: '我们放风筝。',
    category: 'hobbies',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['kites'],
    blanks: [2]
  },
  {
    id: 'elem_g4_hob_004',
    sentence: 'He likes music.',
    translation: '他喜欢音乐。',
    category: 'hobbies',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['music'],
    blanks: [2]
  },
  {
    id: 'elem_g4_hob_005',
    sentence: 'I read comic books.',
    translation: '我看漫画书。',
    category: 'hobbies',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['comic'],
    blanks: [2]
  },

  // Questions and Answers
  {
    id: 'elem_g4_qa_001',
    sentence: 'What is your name?',
    translation: '你叫什么名字？',
    category: 'social',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['name'],
    blanks: [3]
  },
  {
    id: 'elem_g4_qa_002',
    sentence: 'Where do you live?',
    translation: '你住在哪里？',
    category: 'social',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['live'],
    blanks: [2]
  },
  {
    id: 'elem_g4_qa_003',
    sentence: 'How old are you?',
    translation: '你几岁了？',
    category: 'social',
    difficulty: 'easy',
    grade: '4',
    level: 'elementary',
    keywords: ['old'],
    blanks: [1]
  },
  {
    id: 'elem_g4_qa_004',
    sentence: 'When is your birthday?',
    translation: '你的生日是什么时候？',
    category: 'social',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['birthday'],
    blanks: [3]
  },
  {
    id: 'elem_g4_qa_005',
    sentence: 'Why are you happy?',
    translation: '你为什么开心？',
    category: 'social',
    difficulty: 'medium',
    grade: '4',
    level: 'elementary',
    keywords: ['happy'],
    blanks: [3]
  },

  // ==================== Elementary Grade 5 - Expanded (Target: 80+ sentences) ====================
  // Past Tense
  {
    id: 'elem_g5_past_001',
    sentence: 'I visited my grandma yesterday.',
    translation: '我昨天看望了我的奶奶。',
    category: 'family',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['visited', 'yesterday'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g5_past_002',
    sentence: 'We played soccer last week.',
    translation: '我们上周踢足球了。',
    category: 'sports',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['played', 'soccer'],
    blanks: [1, 2]
  },
  {
    id: 'elem_g5_past_003',
    sentence: 'She watched TV last night.',
    translation: '她昨晚看电视了。',
    category: 'daily',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['watched', 'night'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g5_past_004',
    sentence: 'They went to the park.',
    translation: '他们去了公园。',
    category: 'places',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['went', 'park'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g5_past_005',
    sentence: 'I ate pizza for dinner.',
    translation: '我晚餐吃了披萨。',
    category: 'food',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['ate', 'pizza'],
    blanks: [1, 2]
  },

  // Future Plans
  {
    id: 'elem_g5_fut_001',
    sentence: 'I will go to Beijing.',
    translation: '我将要去北京。',
    category: 'travel',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['Beijing'],
    blanks: [4]
  },
  {
    id: 'elem_g5_fut_002',
    sentence: 'She is going to visit her aunt.',
    translation: '她要去看望她的阿姨。',
    category: 'family',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['visit', 'aunt'],
    blanks: [4, 6]
  },
  {
    id: 'elem_g5_fut_003',
    sentence: 'We will have a party.',
    translation: '我们将举办一个派对。',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['party'],
    blanks: [4]
  },
  {
    id: 'elem_g5_fut_004',
    sentence: 'He is going to learn piano.',
    translation: '他要学钢琴。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['piano'],
    blanks: [5]
  },
  {
    id: 'elem_g5_fut_005',
    sentence: 'I will finish my homework soon.',
    translation: '我很快就会完成作业。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['homework'],
    blanks: [3]
  },

  // Comparisons
  {
    id: 'elem_g5_comp_001',
    sentence: 'My brother is taller than me.',
    translation: '我哥哥比我高。',
    category: 'family',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['taller'],
    blanks: [3]
  },
  {
    id: 'elem_g5_comp_002',
    sentence: 'This book is more interesting.',
    translation: '这本书更有趣。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['interesting'],
    blanks: [4]
  },
  {
    id: 'elem_g5_comp_003',
    sentence: 'Summer is hotter than spring.',
    translation: '夏天比春天热。',
    category: 'weather',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['hotter'],
    blanks: [2]
  },
  {
    id: 'elem_g5_comp_004',
    sentence: 'The elephant is the biggest animal.',
    translation: '大象是最大的动物。',
    category: 'animals',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['biggest'],
    blanks: [3]
  },
  {
    id: 'elem_g5_comp_005',
    sentence: 'English is easier than math.',
    translation: '英语比数学简单。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['easier'],
    blanks: [2]
  },

  // Likes and Preferences
  {
    id: 'elem_g5_like_001',
    sentence: 'I prefer tea to coffee.',
    translation: '比起咖啡，我更喜欢茶。',
    category: 'food',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['prefer', 'tea'],
    blanks: [1, 2]
  },
  {
    id: 'elem_g5_like_002',
    sentence: 'She loves reading books.',
    translation: '她喜欢读书。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['loves', 'reading'],
    blanks: [1, 2]
  },
  {
    id: 'elem_g5_like_003',
    sentence: 'My favorite color is blue.',
    translation: '我最喜欢的颜色是蓝色。',
    category: 'daily',
    difficulty: 'easy',
    grade: '5',
    level: 'elementary',
    keywords: ['favorite', 'blue'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g5_like_004',
    sentence: 'I enjoy playing games.',
    translation: '我喜欢玩游戏。',
    category: 'activities',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['enjoy', 'games'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g5_like_005',
    sentence: 'He likes to help others.',
    translation: '他喜欢帮助别人。',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['help'],
    blanks: [3]
  },

  // Daily Schedule
  {
    id: 'elem_g5_sch_001',
    sentence: 'I usually wake up at six.',
    translation: '我通常六点起床。',
    category: 'daily',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['wake', 'six'],
    blanks: [2, 5]
  },
  {
    id: 'elem_g5_sch_002',
    sentence: 'School starts at eight thirty.',
    translation: '学校八点半开始上课。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['starts', 'eight'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g5_sch_003',
    sentence: 'We have lunch at twelve.',
    translation: '我们十二点吃午饭。',
    category: 'food',
    difficulty: 'easy',
    grade: '5',
    level: 'elementary',
    keywords: ['lunch', 'twelve'],
    blanks: [2, 4]
  },
  {
    id: 'elem_g5_sch_004',
    sentence: 'I do homework after school.',
    translation: '我放学后做作业。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['homework', 'school'],
    blanks: [2, 4]
  },
  {
    id: 'elem_g5_sch_005',
    sentence: 'I go to bed at nine.',
    translation: '我九点上床睡觉。',
    category: 'daily',
    difficulty: 'easy',
    grade: '5',
    level: 'elementary',
    keywords: ['bed', 'nine'],
    blanks: [3, 5]
  },

  // Present Continuous
  {
    id: 'elem_g5_cont_001',
    sentence: 'I am reading a book now.',
    translation: '我现在正在读书。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['reading'],
    blanks: [2]
  },
  {
    id: 'elem_g5_cont_002',
    sentence: 'She is cooking dinner.',
    translation: '她正在做晚饭。',
    category: 'food',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['cooking'],
    blanks: [2]
  },
  {
    id: 'elem_g5_cont_003',
    sentence: 'They are playing basketball.',
    translation: '他们正在打篮球。',
    category: 'sports',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['playing', 'basketball'],
    blanks: [2, 3]
  },
  {
    id: 'elem_g5_cont_004',
    sentence: 'The dog is running fast.',
    translation: '那只狗正在快速奔跑。',
    category: 'animals',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['running'],
    blanks: [2]
  },
  {
    id: 'elem_g5_cont_005',
    sentence: 'We are studying English.',
    translation: '我们正在学英语。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['studying', 'English'],
    blanks: [2, 3]
  },

  // Abilities and Skills
  {
    id: 'elem_g5_abi_001',
    sentence: 'I can speak English well.',
    translation: '我能说好英语。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['speak', 'English'],
    blanks: [2, 3]
  },
  {
    id: 'elem_g5_abi_002',
    sentence: 'She can play the violin.',
    translation: '她会拉小提琴。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['violin'],
    blanks: [4]
  },
  {
    id: 'elem_g5_abi_003',
    sentence: 'He cannot swim fast.',
    translation: '他游得不快。',
    category: 'sports',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['swim'],
    blanks: [2]
  },
  {
    id: 'elem_g5_abi_004',
    sentence: 'We can help each other.',
    translation: '我们可以互相帮助。',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['help'],
    blanks: [2]
  },
  {
    id: 'elem_g5_abi_005',
    sentence: 'I could ride a bike when I was five.',
    translation: '我五岁时就会骑自行车了。',
    category: 'activities',
    difficulty: 'hard',
    grade: '5',
    level: 'elementary',
    keywords: ['bike', 'five'],
    blanks: [3, 8]
  },

  // Questions and Requests
  {
    id: 'elem_g5_req_001',
    sentence: 'Can you help me please?',
    translation: '你能帮我一下吗？',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['help'],
    blanks: [2]
  },
  {
    id: 'elem_g5_req_002',
    sentence: 'Would you like some tea?',
    translation: '你想要一些茶吗？',
    category: 'food',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['tea'],
    blanks: [4]
  },
  {
    id: 'elem_g5_req_003',
    sentence: 'Could you open the door?',
    translation: '你能打开门吗？',
    category: 'daily',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['door'],
    blanks: [4]
  },
  {
    id: 'elem_g5_req_004',
    sentence: 'May I come in?',
    translation: '我可以进来吗？',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['come'],
    blanks: [2]
  },
  {
    id: 'elem_g5_req_005',
    sentence: 'Should we go now?',
    translation: '我们应该走了吗？',
    category: 'daily',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['now'],
    blanks: [3]
  },

  // Health and Feelings
  {
    id: 'elem_g5_feel_001',
    sentence: 'I feel tired today.',
    translation: '我今天感觉很累。',
    category: 'health',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['tired'],
    blanks: [2]
  },
  {
    id: 'elem_g5_feel_002',
    sentence: 'She looks happy.',
    translation: '她看起来很开心。',
    category: 'social',
    difficulty: 'easy',
    grade: '5',
    level: 'elementary',
    keywords: ['happy'],
    blanks: [2]
  },
  {
    id: 'elem_g5_feel_003',
    sentence: 'He seems worried.',
    translation: '他看起来很担心。',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['worried'],
    blanks: [2]
  },
  {
    id: 'elem_g5_feel_004',
    sentence: 'I am excited about the trip.',
    translation: '我对这次旅行很兴奋。',
    category: 'travel',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['excited', 'trip'],
    blanks: [2, 5]
  },
  {
    id: 'elem_g5_feel_005',
    sentence: 'She feels sad about the news.',
    translation: '她对这则消息感到难过。',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['sad', 'news'],
    blanks: [2, 5]
  },

  // ==================== Elementary Grade 6 - Expanded (Target: 80+ sentences) ====================
  // Present Perfect
  {
    id: 'elem_g6_perf_001',
    sentence: 'I have finished my homework.',
    translation: '我已经完成了作业。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['finished'],
    blanks: [2]
  },
  {
    id: 'elem_g6_perf_002',
    sentence: 'She has visited Beijing.',
    translation: '她去过北京。',
    category: 'travel',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['visited', 'Beijing'],
    blanks: [2, 3]
  },
  {
    id: 'elem_g6_perf_003',
    sentence: 'We have learned English for three years.',
    translation: '我们学英语已经三年了。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['learned', 'English'],
    blanks: [2, 3]
  },
  {
    id: 'elem_g6_perf_004',
    sentence: 'He has never been to Japan.',
    translation: '他从未去过日本。',
    category: 'travel',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['Japan'],
    blanks: [5]
  },
  {
    id: 'elem_g6_perf_005',
    sentence: 'I have lost my key.',
    translation: '我把钥匙丢了。',
    category: 'daily',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['lost', 'key'],
    blanks: [2, 4]
  },

  // Conditionals
  {
    id: 'elem_g6_cond_001',
    sentence: 'If it rains, I will stay home.',
    translation: '如果下雨，我就待在家里。',
    category: 'weather',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['rains', 'home'],
    blanks: [2, 6]
  },
  {
    id: 'elem_g6_cond_002',
    sentence: 'If you study hard, you will pass.',
    translation: '如果你努力学习，你就会通过。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['study', 'pass'],
    blanks: [2, 6]
  },
  {
    id: 'elem_g6_cond_003',
    sentence: 'If I have time, I will read books.',
    translation: '如果我有时间，我就读书。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['time', 'read'],
    blanks: [2, 6]
  },
  {
    id: 'elem_g6_cond_004',
    sentence: 'If she comes, we will play together.',
    translation: '如果她来，我们就一起玩。',
    category: 'social',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['comes', 'play'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g6_cond_005',
    sentence: 'If the weather is good, we will go out.',
    translation: '如果天气好，我们就出去。',
    category: 'weather',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['weather', 'out'],
    blanks: [2, 8]
  },

  // Passive Voice (Simple)
  {
    id: 'elem_g6_pass_001',
    sentence: 'English is spoken here.',
    translation: '这里说英语。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['English', 'spoken'],
    blanks: [0, 2]
  },
  {
    id: 'elem_g6_pass_002',
    sentence: 'The book was written by a teacher.',
    translation: '这本书是一位老师写的。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['written', 'teacher'],
    blanks: [2, 6]
  },
  {
    id: 'elem_g6_pass_003',
    sentence: 'The window was closed.',
    translation: '窗户被关上了。',
    category: 'daily',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['window', 'closed'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g6_pass_004',
    sentence: 'The homework is done.',
    translation: '作业做完了。',
    category: 'school',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['homework', 'done'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g6_pass_005',
    sentence: 'The cake was made by mom.',
    translation: '蛋糕是妈妈做的。',
    category: 'food',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['cake', 'made'],
    blanks: [1, 3]
  },

  // Relative Clauses
  {
    id: 'elem_g6_rel_001',
    sentence: 'The boy who is tall is my friend.',
    translation: '那个高个子男孩是我的朋友。',
    category: 'social',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['tall', 'friend'],
    blanks: [4, 7]
  },
  {
    id: 'elem_g6_rel_002',
    sentence: 'The book which I read is interesting.',
    translation: '我读的那本书很有趣。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['book', 'interesting'],
    blanks: [1, 6]
  },
  {
    id: 'elem_g6_rel_003',
    sentence: 'The girl who sings well is my sister.',
    translation: '那个唱歌好听的女孩是我的妹妹。',
    category: 'family',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['sings', 'sister'],
    blanks: [2, 7]
  },
  {
    id: 'elem_g6_rel_004',
    sentence: 'The place where we met is the park.',
    translation: '我们见面的地方是公园。',
    category: 'places',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['place', 'park'],
    blanks: [1, 8]
  },
  {
    id: 'elem_g6_rel_005',
    sentence: 'The movie that we watched was funny.',
    translation: '我们看的电影很有趣。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['movie', 'funny'],
    blanks: [1, 7]
  },

  // Adverbs and Frequency
  {
    id: 'elem_g6_adv_001',
    sentence: 'I always brush my teeth.',
    translation: '我总是刷牙。',
    category: 'health',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['always', 'teeth'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g6_adv_002',
    sentence: 'She usually gets up early.',
    translation: '她通常起得很早。',
    category: 'daily',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['usually', 'early'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g6_adv_003',
    sentence: 'We often go swimming.',
    translation: '我们经常去游泳。',
    category: 'sports',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['often', 'swimming'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g6_adv_004',
    sentence: 'He sometimes plays games.',
    translation: '他有时玩游戏。',
    category: 'activities',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['sometimes', 'games'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g6_adv_005',
    sentence: 'I never eat junk food.',
    translation: '我从不吃垃圾食品。',
    category: 'food',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['never', 'food'],
    blanks: [1, 4]
  },

  // Modal Verbs
  {
    id: 'elem_g6_modal_001',
    sentence: 'You must finish your homework.',
    translation: '你必须完成你的作业。',
    category: 'school',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['must', 'homework'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g6_modal_002',
    sentence: 'We should help our parents.',
    translation: '我们应该帮助我们的父母。',
    category: 'family',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['should', 'parents'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g6_modal_003',
    sentence: 'You need to drink more water.',
    translation: '你需要多喝水。',
    category: 'health',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['need', 'water'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g6_modal_004',
    sentence: 'Children should not watch too much TV.',
    translation: '孩子不应该看太多电视。',
    category: 'daily',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['should', 'TV'],
    blanks: [1, 7]
  },
  {
    id: 'elem_g6_modal_005',
    sentence: 'We must protect our environment.',
    translation: '我们必须保护我们的环境。',
    category: 'nature',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['must', 'environment'],
    blanks: [1, 4]
  },

  // Exclamations
  {
    id: 'elem_g6_excl_001',
    sentence: 'What a beautiful day!',
    translation: '多么美好的一天啊！',
    category: 'weather',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['beautiful', 'day'],
    blanks: [2, 3]
  },
  {
    id: 'elem_g6_excl_002',
    sentence: 'How fast he runs!',
    translation: '他跑得真快啊！',
    category: 'sports',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['fast', 'runs'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g6_excl_003',
    sentence: 'What a nice surprise!',
    translation: '多么好的惊喜啊！',
    category: 'social',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['nice', 'surprise'],
    blanks: [2, 3]
  },
  {
    id: 'elem_g6_excl_004',
    sentence: 'How clever you are!',
    translation: '你真聪明啊！',
    category: 'wisdom',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['clever'],
    blanks: [1]
  },
  {
    id: 'elem_g6_excl_005',
    sentence: 'What an interesting story!',
    translation: '多么有趣的故事啊！',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['interesting', 'story'],
    blanks: [2, 3]
  },

  // Plans and Goals
  {
    id: 'elem_g6_plan_001',
    sentence: 'I want to be a doctor.',
    translation: '我想当一名医生。',
    category: 'future',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['doctor'],
    blanks: [4]
  },
  {
    id: 'elem_g6_plan_002',
    sentence: 'She hopes to travel around the world.',
    translation: '她希望环游世界。',
    category: 'travel',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['travel', 'world'],
    blanks: [2, 6]
  },
  {
    id: 'elem_g6_plan_003',
    sentence: 'My dream is to become a teacher.',
    translation: '我的梦想是成为一名老师。',
    category: 'future',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['dream', 'teacher'],
    blanks: [1, 6]
  },
  {
    id: 'elem_g6_plan_004',
    sentence: 'He plans to study harder next year.',
    translation: '他计划明年更努力学习。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['study', 'year'],
    blanks: [3, 6]
  },
  {
    id: 'elem_g6_plan_005',
    sentence: 'We plan to visit Shanghai this summer.',
    translation: '我们计划今年夏天去上海。',
    category: 'travel',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['visit', 'Shanghai'],
    blanks: [2, 3]
  },

  // ==================== Elementary Grade 5 - Additional ====================
  {
    id: 'elem_g5_add_001',
    sentence: 'I brush my teeth twice a day.',
    translation: '我每天刷两次牙。',
    category: 'health',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['teeth'],
    blanks: [3]
  },
  {
    id: 'elem_g5_add_002',
    sentence: 'My family has dinner together.',
    translation: '我的家人一起吃晚饭。',
    category: 'family',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['dinner'],
    blanks: [3]
  },
  {
    id: 'elem_g5_add_003',
    sentence: 'We need to buy some vegetables.',
    translation: '我们需要买一些蔬菜。',
    category: 'food',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['vegetables'],
    blanks: [5]
  },
  {
    id: 'elem_g5_add_004',
    sentence: 'The library has many books.',
    translation: '图书馆有很多书。',
    category: 'places',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['library', 'books'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g5_add_005',
    sentence: 'I practice piano every day.',
    translation: '我每天都练习钢琴。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['piano'],
    blanks: [2]
  },
  {
    id: 'elem_g5_add_006',
    sentence: 'She teaches us English.',
    translation: '她教我们英语。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['teaches', 'English'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g5_add_007',
    sentence: 'The movie starts at seven.',
    translation: '电影七点开始。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['movie', 'seven'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g5_add_008',
    sentence: 'I want to learn swimming.',
    translation: '我想学游泳。',
    category: 'sports',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['swimming'],
    blanks: [4]
  },
  {
    id: 'elem_g5_add_009',
    sentence: 'The bus comes every ten minutes.',
    translation: '公交车每十分钟来一趟。',
    category: 'transport',
    difficulty: 'hard',
    grade: '5',
    level: 'elementary',
    keywords: ['bus', 'minutes'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g5_add_010',
    sentence: 'We should keep our classroom clean.',
    translation: '我们应该保持教室整洁。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['classroom', 'clean'],
    blanks: [4, 5]
  },
  {
    id: 'elem_g5_add_011',
    sentence: 'My uncle works in a hospital.',
    translation: '我的叔叔在医院工作。',
    category: 'places',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['uncle', 'hospital'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g5_add_012',
    sentence: 'I send emails to my friends.',
    translation: '我给朋友发邮件。',
    category: 'technology',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['emails', 'friends'],
    blanks: [2, 5]
  },
  {
    id: 'elem_g5_add_013',
    sentence: 'The flower smells nice.',
    translation: '这朵花闻起来很香。',
    category: 'nature',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['flower', 'nice'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g5_add_014',
    sentence: 'We need to save water.',
    translation: '我们需要节约用水。',
    category: 'nature',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['save', 'water'],
    blanks: [3, 4]
  },
  {
    id: 'elem_g5_add_015',
    sentence: 'I help my mom clean the house.',
    translation: '我帮妈妈打扫房子。',
    category: 'daily',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['clean', 'house'],
    blanks: [4, 6]
  },
  {
    id: 'elem_g5_add_016',
    sentence: 'The soup tastes delicious.',
    translation: '汤尝起来很美味。',
    category: 'food',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['soup', 'delicious'],
    blanks: [1, 3]
  },
  {
    id: 'elem_g5_add_017',
    sentence: 'She draws beautiful pictures.',
    translation: '她画的画很漂亮。',
    category: 'hobbies',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['pictures'],
    blanks: [3]
  },
  {
    id: 'elem_g5_add_018',
    sentence: 'I want to make new friends.',
    translation: '我想交新朋友。',
    category: 'social',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['friends'],
    blanks: [5]
  },
  {
    id: 'elem_g5_add_019',
    sentence: 'The museum is closed on Monday.',
    translation: '博物馆周一闭馆。',
    category: 'places',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['museum', 'Monday'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g5_add_020',
    sentence: 'We should respect our teachers.',
    translation: '我们应该尊敬老师。',
    category: 'school',
    difficulty: 'medium',
    grade: '5',
    level: 'elementary',
    keywords: ['respect', 'teachers'],
    blanks: [2, 4]
  },

  // ==================== Elementary Grade 6 - Additional ====================
  {
    id: 'elem_g6_add_001',
    sentence: 'I have been to Shanghai twice.',
    translation: '我去过上海两次。',
    category: 'travel',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['Shanghai', 'twice'],
    blanks: [3, 5]
  },
  {
    id: 'elem_g6_add_002',
    sentence: 'The meeting will start soon.',
    translation: '会议很快就要开始了。',
    category: 'daily',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['meeting', 'start'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g6_add_003',
    sentence: 'She has already finished reading.',
    translation: '她已经读完书了。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['finished', 'reading'],
    blanks: [3, 4]
  },
  {
    id: 'elem_g6_add_004',
    sentence: 'We should protect wild animals.',
    translation: '我们应该保护野生动物。',
    category: 'animals',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['protect', 'animals'],
    blanks: [2, 4]
  },
  {
    id: 'elem_g6_add_005',
    sentence: 'The book which I bought is very good.',
    translation: '我买的那本书非常好。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['book', 'good'],
    blanks: [1, 7]
  },
  {
    id: 'elem_g6_add_006',
    sentence: 'I was reading when you called.',
    translation: '你打电话时我正在读书。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['reading', 'called'],
    blanks: [1, 5]
  },
  {
    id: 'elem_g6_add_007',
    sentence: 'The work was done by the students.',
    translation: '这项工作是学生们完成的。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['work', 'students'],
    blanks: [1, 6]
  },
  {
    id: 'elem_g6_add_008',
    sentence: 'We have learned about different cultures.',
    translation: '我们学习了不同的文化。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['cultures'],
    blanks: [5]
  },
  {
    id: 'elem_g6_add_009',
    sentence: 'She is taller than her sister.',
    translation: '她比她妹妹高。',
    category: 'family',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['taller', 'sister'],
    blanks: [2, 5]
  },
  {
    id: 'elem_g6_add_010',
    sentence: 'I will call you tomorrow evening.',
    translation: '我明天晚上给你打电话。',
    category: 'social',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['call', 'tomorrow'],
    blanks: [2, 4]
  },
  {
    id: 'elem_g6_add_011',
    sentence: 'The movie which we watched was exciting.',
    translation: '我们看的电影很激动人心。',
    category: 'hobbies',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['movie', 'exciting'],
    blanks: [1, 7]
  },
  {
    id: 'elem_g6_add_012',
    sentence: 'If you are tired, you should rest.',
    translation: '如果你累了，就应该休息。',
    category: 'health',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['tired', 'rest'],
    blanks: [2, 6]
  },
  {
    id: 'elem_g6_add_013',
    sentence: 'The problem has been solved.',
    translation: '问题已经解决了。',
    category: 'daily',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['problem', 'solved'],
    blanks: [1, 4]
  },
  {
    id: 'elem_g6_add_014',
    sentence: 'We need to recycle paper.',
    translation: '我们需要回收纸张。',
    category: 'nature',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['recycle', 'paper'],
    blanks: [3, 4]
  },
  {
    id: 'elem_g6_add_015',
    sentence: 'The teacher who teaches us is very kind.',
    translation: '教我们的老师很和蔼。',
    category: 'school',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['teacher', 'kind'],
    blanks: [1, 8]
  },
  {
    id: 'elem_g6_add_016',
    sentence: 'I have never seen such a big tree.',
    translation: '我从未见过这么大的树。',
    category: 'nature',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['tree'],
    blanks: [6]
  },
  {
    id: 'elem_g6_add_017',
    sentence: 'The cake was made by my grandmother.',
    translation: '蛋糕是我奶奶做的。',
    category: 'food',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['cake', 'grandmother'],
    blanks: [1, 6]
  },
  {
    id: 'elem_g6_add_018',
    sentence: 'We are going to visit the museum.',
    translation: '我们要去参观博物馆。',
    category: 'places',
    difficulty: 'medium',
    grade: '6',
    level: 'elementary',
    keywords: ['visit', 'museum'],
    blanks: [4, 6]
  },
  {
    id: 'elem_g6_add_019',
    sentence: 'The boy who is playing is my brother.',
    translation: '正在玩耍的那个男孩是我的弟弟。',
    category: 'family',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['playing', 'brother'],
    blanks: [3, 7]
  },
  {
    id: 'elem_g6_add_020',
    sentence: 'If it snows, we will make a snowman.',
    translation: '如果下雪，我们就堆雪人。',
    category: 'weather',
    difficulty: 'hard',
    grade: '6',
    level: 'elementary',
    keywords: ['snows', 'snowman'],
    blanks: [2, 7]
  }
];

// Helper functions
export function getSentencesByDifficulty(difficulty: 'easy' | 'medium' | 'hard'): ListeningSentence[] {
  return listeningSentences.filter(s => s.difficulty === difficulty);
}

export function getSentencesByGrade(grade: '3' | '4' | '5' | '6' | '7' | '8' | '9'): ListeningSentence[] {
  return listeningSentences.filter(s => s.grade === grade);
}

export function getSentencesByLevel(level: 'elementary' | 'middle'): ListeningSentence[] {
  return listeningSentences.filter(s => s.level === level);
}

export function getSentencesByCategory(category: string): ListeningSentence[] {
  return listeningSentences.filter(s => s.category === category);
}

export function getRandomSentences(count: number): ListeningSentence[] {
  const shuffled = [...listeningSentences].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export function getSentenceById(id: string): ListeningSentence | undefined {
  return listeningSentences.find(s => s.id === id);
}

// Get categories
export const listeningCategories = [
  { id: 'daily', name: '日常生活', icon: '🏠' },
  { id: 'food', name: '食物', icon: '🍎' },
  { id: 'family', name: '家庭', icon: '👨‍👩‍👧‍👦' },
  { id: 'animals', name: '动物', icon: '🐾' },
  { id: 'school', name: '学校', icon: '🏫' },
  { id: 'greetings', name: '问候', icon: '👋' },
  { id: 'weather', name: '天气', icon: '☀️' },
  { id: 'social', name: '社交', icon: '👥' },
  { id: 'activities', name: '活动', icon: '🎯' },
  { id: 'places', name: '地点', icon: '📍' },
  { id: 'transport', name: '交通', icon: '🚌' },
  { id: 'hobbies', name: '爱好', icon: '🎨' },
  { id: 'sports', name: '运动', icon: '⚽' },
  { id: 'travel', name: '旅行', icon: '✈️' },
  { id: 'technology', name: '科技', icon: '💻' },
  { id: 'health', name: '健康', icon: '💪' },
  { id: 'future', name: '未来', icon: '🌟' },
  { id: 'wisdom', name: '智慧', icon: '💡' },
  { id: 'nature', name: '自然', icon: '🌿' },
  { id: 'achievements', name: '成就', icon: '🏆' }
];
